import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";

import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { companyBasicDetailDummyValues, foreignKeys, supplierDrawerElements } from "data/supplierPageData";
import CompanyDetails from "./CompanyDetails/CompanyDetails";
import AllFactories from "./AllFactories/AllFactories";
import SupplierStatus from "./SupplierStatus/supplierStatus";
import axios from "axios";
import { GlobalState } from "context/GlobalContext";
import FactoryDetails from "./FactoryDetails/FactoryDetails";
import Agreements from "./Agreemens/Agreements";
import { AuthState } from "context/AuthContext";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import { APPROVAL_ACTIONS, getLatestApproval, mergeApprovalRow } from "utils/supplierApprovalHelpers";

function SupplierUser() {
	const [pathName] = useOutletContext();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const tab = pathName;
	const setTab = (key) => {
		const qs = searchParams.toString();
		navigate(`../${key}${qs ? `?${qs}` : ""}`);
	};
	const [companyData, setCompanyData] = useState();
	const [factories, setFactories] = useState();
	const [selectedFactory, setSelectedFactory] = useState();
	const [isOnboarded, setIsOnboarded] = useState(false);
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();

	const openPendingCycle = async ({ type, isFactory, pageData }) => {
		const rows = isFactory ? selectedFactory?.factoryApproval : companyData?.supplierApproval;
		const latest = getLatestApproval(rows, type);
		// Only open a new cycle when latest is Rejected (preserve remark). Pending/Approved no-op for edit reset.
		if (!latest?.remark || latest.approved) {
			if (typeof pageData !== "undefined") {
				if (isFactory) setSelectedFactory(pageData);
				else setCompanyData(pageData);
			}
			return;
		}

		setLoadingOpen(true);
		try {
			const res = await axios.post(`/api/supplier/approval_action`, {
				action: APPROVAL_ACTIONS.OPEN_PENDING,
				type,
				supplier_id: isFactory ? null : companyData?.id,
				factory_id: isFactory ? selectedFactory?.id : null,
				updated_by: user?.email,
			});
			const row = res.data.result;
			if (isFactory) {
				const updatedFactory = {
					...(pageData || selectedFactory),
					factoryApproval: mergeApprovalRow((pageData || selectedFactory)?.factoryApproval, row),
				};
				setSelectedFactory(updatedFactory);
				setFactories((prev) => (prev || []).map((fact) => (fact.id === updatedFactory.id ? updatedFactory : fact)));
			} else {
				setCompanyData({
					...(pageData || companyData),
					supplierApproval: mergeApprovalRow((pageData || companyData)?.supplierApproval, row),
					...(res.data.cleared_final_approval ? { is_approved: false } : {}),
				});
			}
		} catch (err) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `ApprovalError: ${parseErrMsgFromError(err)}`,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
		setLoadingOpen(false);
	};

	const handleEditSApprovalDb = (type, pageData) => {
		openPendingCycle({ type, isFactory: false, pageData });
	};
	const handleEditFApprovalDb = (type, pageData) => {
		openPendingCycle({ type, isFactory: true, pageData });
	};

	const tabComponents = [
		{
			...supplierDrawerElements.company_info,
			component: (
				<CompanyDetails
					pageData={companyData}
					setPageData={setCompanyData}
					isOnboarded={isOnboarded}
					setIsOnboarded={setIsOnboarded}
					foreignKey={foreignKeys.supplier}
					disableEdit={false}
					handleEditSApprovalDb={(type, pageData) => handleEditSApprovalDb(type, pageData)}
				/>
			),
		},
		{
			...supplierDrawerElements.all_factories,
			component: (
				<AllFactories
					factories={factories}
					setFactories={setFactories}
					selectedFactory={selectedFactory}
					setSelectedFactory={setSelectedFactory}
					companyData={companyData}
					setTab={setTab}
					disableEdit={false}
				/>
			),
		},
		{
			...supplierDrawerElements.factory,
			component: (
				<FactoryDetails
					key={selectedFactory?.id}
					foreignKey={foreignKeys.factory}
					pageData={selectedFactory}
					setPageData={setSelectedFactory}
					companyData={companyData}
					setTab={setTab}
					disableEdit={false}
					handleEditFApprovalDb={(type, pageData) => handleEditFApprovalDb(type, pageData)}
				/>
			),
		},
		{
			...supplierDrawerElements.agreements,
			component: (
				<Agreements
					key={selectedFactory?.id}
					foreignKey={foreignKeys.factory}
					disableEdit={false}
					companyData={companyData}
					setCompanyData={setCompanyData}
				/>
			),
		},
		{
			...supplierDrawerElements.status,
			component: (
				<SupplierStatus
					foreignKey={foreignKeys.factory}
					allFactories={factories}
					setPageData={setSelectedFactory}
					companyData={companyData}
					setTab={setTab}
				/>
			),
		},
	];
	useEffect(() => {
		const fetch = async () => {
			setLoadingOpen(true);
			await axios
				.get("/api/supplier/find_or_create")
				.then((res) => {
					let data = res.data;
					if (data.created) {
						setCompanyData({
							...companyBasicDetailDummyValues,
							...data.result,
							supplierApproval: data.result.supplierApproval || [],
						});
						setFactories(data.result.supplierFactories || []);
						setIsOnboarded(false);
					} else {
						setCompanyData(data.result);
						setFactories(data.result.supplierFactories || []);
						setIsOnboarded(data.result.is_onboarded);
					}
				})
				.catch((err) => {
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: parseErrMsgFromError(err),
						severity: "error",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
				});
			setLoadingOpen(false);
		};
		fetch();
	}, []);

	return (
		<Box sx={{ width: "100%", minHeight: "calc(100vh - 64px)" }}>
			{companyData &&
				tabComponents?.map((row) => (
					<Box
						key={row.key}
						display={row.key === tab ? "flex" : "none"}
						sx={{
							width: "100%",
							minHeight: "calc(100vh - 64px)",
							flexDirection: "column",
						}}>
						<Box
							sx={{
								width: "100%",
								flex: 1,
								minHeight: 0,
								bgcolor: "#F9FAFB",
								px: { xs: 1.5, md: 2.5 },
								py: { xs: 2, md: 2.5 },
								boxSizing: "border-box",
								display: "flex",
								flexDirection: "column",
							}}>
							<Box
								sx={{
									width: "100%",
									maxWidth: 1280,
									mx: "auto",
									flex: 1,
									minHeight: 0,
									display: "flex",
									flexDirection: "column",
								}}>
								{row.component}
							</Box>
						</Box>
					</Box>
				))}
		</Box>
	);
}

export default SupplierUser;
